import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, ReplaySubject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import moment from 'moment/moment';
import {FormHelperService} from '../../../../shared/services/form-helper.service';
import {ChronieMaintenanceWindowService} from '../../../../shared/services/chronie-maintenance-window.service';
import {
  ChronieMaintenanceWindow,
  MaintenanceWindowInsuranceCompany, MaintenanceWindowListItem,
  MaintenanceWindowPayload,
} from '../../../../shared/interface/chronie-maintenance-window';

interface TemplateVariable {
  variable: string;
  description: string;
  example: string;
}

@Component({
  selector: 'app-chronie-maintenance-window-add',
  templateUrl: './chronie-maintenance-window-add.component.html',
  styleUrls: ['./chronie-maintenance-window-add.component.scss']
})
export class ChronieMaintenanceWindowAddComponent implements OnInit, OnDestroy {
  @Input() maintenance?: Observable<MaintenanceWindowListItem>;
  @Output() closeModification = new EventEmitter<any>();

  form: FormGroup;

  enabledControl: FormControl;
  noticeEnabledControl: FormControl;
  titleControl: FormControl;
  messageControl: FormControl;

  displayFromControl: FormControl;
  displayFromTimeControl: FormControl;
  displayToControl: FormControl;
  displayToTimeControl: FormControl;

  startsAtControl: FormControl;
  startsAtTimeControl: FormControl;
  endsAtControl: FormControl;
  endsAtTimeControl: FormControl;

  blockPurchasesControl: FormControl;
  insuranceCompanyIdControl: FormControl;

  maintenanceWindowId: string | null = null;
  currentObject: ChronieMaintenanceWindow | null = null;
  insuranceCompanies: MaintenanceWindowInsuranceCompany[] = [];

  processingRequest = false;
  loading = false;
  errors: any = {};

  backendAlertMessage: string | null = null;

  readonly variablesDisplayedColumns: string[] = ['variable', 'description', 'example'];

  readonly variables: TemplateVariable[] = [
    {variable: '{maintenanceRange}', description: 'Pełny zakres czasu wdrożenia', example: '02.10.2026 r. w godz. 22:30–23:30'},
    {variable: '{maintenanceStartDate}', description: 'Data rozpoczęcia wdrożenia', example: '02.10.2026'},
    {variable: '{maintenanceStartTime}', description: 'Godzina rozpoczęcia wdrożenia', example: '22:30'},
    {variable: '{maintenanceEndDate}', description: 'Data zakończenia wdrożenia', example: '02.10.2026'},
    {variable: '{maintenanceEndTime}', description: 'Godzina zakończenia wdrożenia', example: '23:30'},
    {variable: '{bannerRange}', description: 'Pełny zakres wyświetlania banera', example: '01.10.2026 r. godz. 08:00 – 02.10.2026 r. godz. 23:30'},
    {variable: '{bannerStartDate}', description: 'Data rozpoczęcia wyświetlania banera', example: '01.10.2026'},
    {variable: '{bannerStartTime}', description: 'Godzina rozpoczęcia wyświetlania banera', example: '08:00'},
    {variable: '{bannerEndDate}', description: 'Data zakończenia wyświetlania banera', example: '02.10.2026'},
    {variable: '{bannerEndTime}', description: 'Godzina zakończenia wyświetlania banera', example: '23:30'},
    {variable: '{insuranceCompany}', description: 'Wybrane towarzystwo ubezpieczeniowe', example: 'InterRisk'},
  ];

  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private chronieMaintenanceWindowService: ChronieMaintenanceWindowService,
    private snackBar: MatSnackBar,
    private formHelper: FormHelperService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.form = this.fb.group({
      enabled: this.enabledControl = this.fb.control(true),
      noticeEnabled: this.noticeEnabledControl = this.fb.control(true),
      title: this.titleControl = this.fb.control('Planowana przerwa techniczna', [Validators.required]),
      message: this.messageControl = this.fb.control(
        'Informujemy, że {maintenanceRange} będą prowadzone planowane prace techniczne. W tym czasie mogą wystąpić chwilowe problemy z dostępnością serwisu. Przepraszamy za ewentualne niedogodności.',
        [Validators.required]
      ),

      displayFrom: this.displayFromControl = this.fb.control(null, [Validators.required]),
      displayFromTime: this.displayFromTimeControl = this.fb.control('08:00', [Validators.required]),
      displayTo: this.displayToControl = this.fb.control(null, [Validators.required]),
      displayToTime: this.displayToTimeControl = this.fb.control('23:30'),

      startsAt: this.startsAtControl = this.fb.control(null, [Validators.required]),
      startsAtTime: this.startsAtTimeControl = this.fb.control('22:30', [Validators.required]),
      endsAt: this.endsAtControl = this.fb.control(null, [Validators.required]),
      endsAtTime: this.endsAtTimeControl = this.fb.control('23:30', [Validators.required]),

      blockPurchases: this.blockPurchasesControl = this.fb.control(true),
      insuranceCompanyId: this.insuranceCompanyIdControl = this.fb.control(0),
    }, {validators: this.validateDateRanges()});
  }

  ngOnInit(): void {
    this.loadInsuranceCompanies();
    this.watchForm();

    this.route.params
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        this.maintenanceWindowId = params.id || null;

        if (this.maintenanceWindowId) {
          this.initialize(this.maintenanceWindowId);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Kalendarze ograniczają WYŁĄCZNIE dni; godzinę uwzględnia walidator kolejności przedziałów.
  get minDisplayFromDate(): moment.Moment {
    return moment().startOf('day');
  }

  get minDisplayToDate(): moment.Moment {
    return this.laterDate(this.minDisplayFromDate, this.dateOnly(this.displayFromControl.value));
  }

  get minStartsAtDate(): moment.Moment {
    return this.laterDate(this.minDisplayFromDate, this.dateOnly(this.displayFromControl.value));
  }

  get maxStartsAtDate(): moment.Moment | null {
    const displayTo = this.dateOnly(this.displayToControl.value);
    const endsAt = this.dateOnly(this.endsAtControl.value);
    if (!displayTo) return endsAt;
    if (!endsAt) return displayTo;
    return displayTo.isBefore(endsAt, 'day') ? displayTo : endsAt;
  }

  get minEndsAtDate(): moment.Moment {
    return this.laterDate(
      this.laterDate(this.minDisplayFromDate, this.dateOnly(this.displayFromControl.value)),
      this.dateOnly(this.startsAtControl.value)
    );
  }

  get maxEndsAtDate(): moment.Moment | null {
    return this.dateOnly(this.displayToControl.value);
  }

  private dateOnly(value: any): moment.Moment | null {
    if (!value) return null;
    const date = moment(value);
    return date.isValid() ? date.startOf('day') : null;
  }

  private laterDate(a: moment.Moment | null, b: moment.Moment | null): moment.Moment | null {
    if (!a) return b;
    if (!b) return a;
    return b.isAfter(a, 'day') ? b : a;
  }

  get isEdit(): boolean {
    return !!this.maintenance || !!this.maintenanceWindowId;
  }

  save(): void {
    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const messages = this.getValidationMessages();

      // Pokazuje faktyczne błędy, także te pochodzące z walidatora FormGroup.
      // Bez logowania wartości pól zawierających treść komunikatu.
      console.warn('[ChronieMaintenanceWindow] Niepoprawny formularz', {
        formErrors: this.form.errors,
        controls: Object.keys(this.form.controls)
          .filter(name => this.form.get(name)?.invalid)
          .map(name => ({name, errors: this.form.get(name)?.errors})),
      });

      const message = messages.length
        ? messages.slice(0, 2).join(' ') + (messages.length > 2 ? ` (+${messages.length - 2} kolejnych błędów)` : '')
        : 'Nie można zapisać formularza. Sprawdź poprawność pól.';

      this.snackBar.open(message, 'Zamknij', {duration: 8000});
      return;
    }

    this.backendAlertMessage = null;
    this.processingRequest = true;

    this.chronieMaintenanceWindowService.sendForm(
      this.buildPayload(),
      this.maintenanceWindowId || undefined,
    )
      .pipe(first())
      .subscribe({
        next: response => {
          this.processingRequest = false;
          this.snackBar.open('Pomyślnie zapisano komunikat techniczny', '', {duration: 2500});

          if (this.maintenance) {
            this.closeModification.emit();
            return;
          }

          this.router.navigate(['/chronie/komunikaty-techniczne/szczegoly', response.uuid]);
        },
        error: (error: HttpErrorResponse) => {
          this.processingRequest = false;

          const text = error?.error?.error?.text
            || error?.error?.error
            || 'Podczas zapisu wystąpił błąd';

          if (error.status === 400) {
            const message = typeof text === 'string'
              ? text
              : 'Niepoprawne dane formularza.';

            if (this.isBannerOverlapError(message)) {
              this.setBannerConflictError(message);
              return;
            }

            this.backendAlertMessage = message;

            if (error?.error?.error?.list) {
              this.errors = error.error.error.list;
              this.formHelper.setBackendErrorState(this.form, this.errors);
            }

            return;
          }

          this.snackBar.open(
            typeof text === 'string' ? text : 'Podczas zapisu wystąpił błąd',
            error.status.toString(),
            {duration: 5000}
          );
        }
      });
  }

  /**
   * Zwraca dokładne przyczyny zablokowania zapisu bez dodatkowych komunikatów
   * pod każdym kalendarzem. Zakresy minDate/maxDate nadal blokują wybór dni.
   */
  private getValidationMessages(): string[] {
    const labels: Record<string, string> = {
      enabled: 'Konfiguracja aktywna',
      noticeEnabled: 'Wyświetlaj baner',
      title: 'Tytuł komunikatu',
      message: 'Treść wiadomości',
      displayFrom: 'Wyświetlaj od',
      displayFromTime: 'Godzina wyświetlania od',
      displayTo: 'Wyświetlaj do',
      displayToTime: 'Godzina wyświetlania do',
      startsAt: 'Data rozpoczęcia wdrożenia',
      startsAtTime: 'Godzina rozpoczęcia wdrożenia',
      endsAt: 'Data zakończenia wdrożenia',
      endsAtTime: 'Godzina zakończenia wdrożenia',
      blockPurchases: 'Blokuj zakup podczas wdrożenia',
      insuranceCompanyId: 'Wszystkie towarzystwa',
    };

    const messages: string[] = [];

    for (const [name, label] of Object.entries(labels)) {
      const control = this.form.get(name);
      if (!control || control.disabled || !control.invalid) continue;

      const errors = control.errors || {};
      if (errors['required']) {
        messages.push(`Uzupełnij pole „${label}”.`);
      } else if (errors['matDatepickerMin']) {
        messages.push(`„${label}”: wybrana data jest wcześniejsza niż dozwolona.`);
      } else if (errors['matDatepickerMax']) {
        messages.push(`„${label}”: wybrana data jest późniejsza niż dozwolona.`);
      } else if (errors['matDatepickerParse']) {
        messages.push(`„${label}”: wprowadź poprawną datę.`);
      } else if (errors['matDatepickerFilter']) {
        messages.push(`„${label}”: ta data nie jest dozwolona.`);
      } else if (errors['backendError']) {
        messages.push(`${label}: ${errors['backendError']}`);
      } else {
        messages.push(`Sprawdź pole „${label}” (błąd: ${Object.keys(errors).join(', ')}).`);
      }
    }

    const groupErrorMessages: Record<string, string> = {
      displayFromPast: 'Baner można zaplanować od dzisiaj. Wybierz dzisiejszą lub przyszłą datę „Wyświetlaj od”.',
      displayToPast: 'Data zakończenia banera nie może być wcześniejsza niż dzisiaj.',
      invalidBannerRange: 'Koniec wyświetlania banera musi być późniejszy niż jego początek (także godzinowo).',
      startsAtPast: 'Data rozpoczęcia wdrożenia nie może być wcześniejsza niż dzisiaj.',
      endsAtPast: 'Data zakończenia wdrożenia nie może być wcześniejsza niż dzisiaj.',
      invalidMaintenanceRange: 'Koniec wdrożenia musi być późniejszy niż jego początek (także godzinowo).',
      maintenanceBeforeBanner: 'Wdrożenie nie może rozpocząć się przed rozpoczęciem wyświetlania banera.',
      maintenanceAfterBanner: 'Wdrożenie nie może zakończyć się po zakończeniu wyświetlania banera.',
    };

    const formErrors = this.form.errors || {};
    for (const [name, message] of Object.entries(groupErrorMessages)) {
      if (formErrors[name]) messages.push(message);
    }

    return messages;
  }

  cancel(): void {
    if (this.maintenance) {
      this.closeModification.emit();
      return;
    }

    this.router.navigate(['/chronie/komunikaty-techniczne']);
  }

  initialize(uuid: string): void {
    this.loading = true;

    this.chronieMaintenanceWindowService.get(uuid)
      .pipe(first())
      .subscribe({
        next: item => {
          this.currentObject = item;
          this.backendAlertMessage = null;

          this.form.reset({
            enabled: item.enabled,
            noticeEnabled: item.noticeEnabled,
            title: item.title,
            message: item.message,

            displayFrom: this.toDateControl(item.displayFrom),
            displayFromTime: this.toTimeControl(item.displayFrom),
            displayTo: item.displayTo ? this.toDateControl(item.displayTo) : null,
            displayToTime: item.displayTo ? this.toTimeControl(item.displayTo) : '23:30',

            startsAt: this.toDateControl(item.startsAt),
            startsAtTime: this.toTimeControl(item.startsAt),
            endsAt: this.toDateControl(item.endsAt),
            endsAtTime: this.toTimeControl(item.endsAt),

            blockPurchases: item.blockPurchases,
            insuranceCompanyId: item.insuranceCompany?.id ?? 0,
          }, {emitEvent: false});

          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Nie udało się pobrać konfiguracji', '', {duration: 4000});
        }
      });
  }

  copyVariable(variable: string): void {
    const textarea = document.createElement('textarea');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.value = variable;
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);

    this.snackBar.open(`Skopiowano ${variable}`, '', {duration: 1500});
  }

  private loadInsuranceCompanies(): void {
    this.chronieMaintenanceWindowService.getInsuranceCompanies()
      .pipe(first())
      .subscribe({
        next: companies => {
          this.insuranceCompanies = companies;
        },
        error: () => {
          this.snackBar.open('Nie udało się pobrać listy towarzystw ubezpieczeniowych', '', {duration: 4000});
        }
      });
  }

  private watchForm(): void {
    this.blockPurchasesControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.form.updateValueAndValidity({emitEvent: false});
      });

    [
      this.enabledControl,
      this.noticeEnabledControl,
      this.displayFromControl,
      this.displayFromTimeControl,
      this.displayToControl,
      this.displayToTimeControl,
      this.insuranceCompanyIdControl,
    ].forEach(control => {
      control.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.clearBannerConflictError();
        });
    });
  }

  private isBannerOverlapError(message: string): boolean {
    return message.includes('jest już zaplanowany aktywny baner');
  }

  private setBannerConflictError(message: string): void {
    this.backendAlertMessage = message;

    [
      this.displayFromControl,
      this.displayFromTimeControl,
      this.displayToControl,
      this.displayToTimeControl,
    ].forEach(control => {
      control.setErrors({
        ...(control.errors || {}),
        backendConflict: true,
      });
      control.markAsTouched();
    });
  }

  private clearBannerConflictError(): void {
    this.backendAlertMessage = null;

    [
      this.displayFromControl,
      this.displayFromTimeControl,
      this.displayToControl,
      this.displayToTimeControl,
    ].forEach(control => {
      if (!control.hasError('backendConflict')) {
        return;
      }

      const errors = {...(control.errors || {})};
      delete errors.backendConflict;

      control.setErrors(
        Object.keys(errors).length > 0
          ? errors
          : null
      );
    });
  }

  /** Kalendarz blokuje przeszłe dni; porównania godzin chronią poprawną kolejność terminów. */
  private validateDateRanges(): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const read = (dateKey: string, timeKey: string): moment.Moment | null => {
        const dateValue = form.get(dateKey)?.value;
        const timeValue = form.get(timeKey)?.value;
        if (!dateValue || !timeValue) {
          return null;
        }
        const date = moment(dateValue);
        if (!date.isValid() || !/^([01]\d|2[0-3]):[0-5]\d$/.test(timeValue)) {
          return null;
        }
        const result = moment(`${date.format('YYYY-MM-DD')} ${timeValue}`, 'YYYY-MM-DD HH:mm', true);
        return result.isValid() ? result : null;
      };

      const displayFrom = read('displayFrom', 'displayFromTime');
      const displayTo = read('displayTo', 'displayToTime');
      const startsAt = read('startsAt', 'startsAtTime');
      const endsAt = read('endsAt', 'endsAtTime');
      const today = moment().startOf('day');
      const errors: ValidationErrors = {};

      // Nie wolno wybierać WCZEŚNIEJSZYCH DNI, ale dzisiaj jest dozwolone
      // niezależnie od godziny. Np. 09.10 godz. 06:00 jest poprawne 09.10 o 12:00.
      if (!this.maintenanceWindowId) {
        if (displayFrom && displayFrom.isBefore(today, 'day')) errors['displayFromPast'] = true;
        if (displayTo && displayTo.isBefore(today, 'day')) errors['displayToPast'] = true;
        if (startsAt && startsAt.isBefore(today, 'day')) errors['startsAtPast'] = true;
        if (endsAt && endsAt.isBefore(today, 'day')) errors['endsAtPast'] = true;
      }

      if (displayFrom && displayTo && !displayTo.isAfter(displayFrom)) {
        errors['invalidBannerRange'] = true;
      }
      if (startsAt && endsAt && !endsAt.isAfter(startsAt)) {
        errors['invalidMaintenanceRange'] = true;
      }
      if (displayFrom && startsAt && startsAt.isBefore(displayFrom)) {
        errors['maintenanceBeforeBanner'] = true;
      }
      if (displayTo && endsAt && endsAt.isAfter(displayTo)) {
        errors['maintenanceAfterBanner'] = true;
      }
      return Object.keys(errors).length ? errors : null;
    };
  }

  private buildPayload(): MaintenanceWindowPayload {
    const raw = this.form.getRawValue();

    return {
      enabled: !!raw.enabled,
      noticeEnabled: !!raw.noticeEnabled,
      title: (raw.title || '').trim(),
      message: (raw.message || '').trim(),
      displayFrom: this.combineDateAndTime(raw.displayFrom, raw.displayFromTime),
      displayTo: raw.displayTo
        ? this.combineDateAndTime(raw.displayTo, raw.displayToTime || '23:30')
        : null,
      startsAt: this.combineDateAndTime(raw.startsAt, raw.startsAtTime),
      endsAt: this.combineDateAndTime(raw.endsAt, raw.endsAtTime),
      blockPurchases: !!raw.blockPurchases,
      insuranceCompanyId: raw.insuranceCompanyId
        ? Number(raw.insuranceCompanyId)
        : null,
    };
  }

  clear(): void {
    this.form.reset({
      enabled: true,
      noticeEnabled: true,
      title: 'Planowana przerwa techniczna',
      message: 'Informujemy, że {maintenanceRange} będą prowadzone planowane prace techniczne. W tym czasie mogą wystąpić chwilowe problemy z dostępnością serwisu. Przepraszamy za ewentualne niedogodności.',
      displayFrom: null,
      displayFromTime: '08:00',
      displayTo: null,
      displayToTime: '23:30',
      startsAt: null,
      startsAtTime: '22:30',
      endsAt: null,
      endsAtTime: '23:30',
      blockPurchases: false,
      insuranceCompanyId: 0,
    }, {emitEvent: false});

    this.errors = {};
    this.clearBannerConflictError();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity({emitEvent: false});
  }

  private combineDateAndTime(dateValue: any, timeValue: string): string {
    const date = moment(dateValue).format('YYYY-MM-DD');
    const time = timeValue || '00:00';

    const localDateTime = moment(
      `${date} ${time}`,
      'YYYY-MM-DD HH:mm',
      true
    )
      .seconds(0)
      .milliseconds(0);

    return localDateTime
      .utc()
      .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
  }

  private toDateControl(value: string): moment.Moment {
    const local = moment.utc(value).local();
    const date = local.format('YYYY-MM-DD');

    // Datepicker ma MomentDateAdapter z useUtc=true,
    // dlatego samą datę przekazujemy jako UTC bez ponownej zmiany dnia.
    return moment.utc(date, 'YYYY-MM-DD');
  }

  private toTimeControl(value: string): string {
    return moment.utc(value)
      .local()
      .format('HH:mm');
  }
}
